import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Heart, Shield, Users } from 'lucide-react';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section 
        className="hero-section relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1458501534264-7d326fa0ca04)'
        }}
      >
        <div className="hero-overlay absolute inset-0"></div>
        <div className="container mx-auto px-4 relative z-10 flex items-center min-h-[80vh]">
          <div className="max-w-3xl fade-in-up">
            <h1 className="text-5xl md:text-6xl font-bold text-dark-text mb-6" data-testid="hero-heading">
              Amrita Sparsha
            </h1>
            <p className="text-2xl md:text-3xl text-dark-text mb-4 font-light">
              Gentle Healing Through Homeopathy & Wellness Care
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Doctor-guided homeopathy and curated wellness products for skin, hair, and holistic health by Amrita Homeopathy.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact" 
                className="btn-primary px-8 py-4 rounded-lg font-semibold text-lg"
                data-testid="hero-consult-button"
              >
                Consult a Doctor
              </Link>
              <Link 
                to="/products" 
                className="border-2 border-yellow-700 text-yellow-700 bg-white px-6 py-3 rounded-lg font-semibold shadow-md hover:bg-yellow-700 hover:text-white transition duration-300"
                data-testid="hero-products-button"
              >
                Explore Products
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-gray-700 leading-relaxed" data-testid="intro-text">
              Amrita Sparsha is a wellness extension of <strong>Amrita Homeopathy</strong>, created to support long-term health through <strong>doctor-guided care and carefully formulated personal wellness products</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mt-4">
              Rooted in classical homeopathy and responsible wellness practices, Amrita Sparsha focuses on <strong>supportive care, skin and hair health, and everyday well-being</strong>—without exaggerated claims or quick fixes.
            </p>
          </div>
        </div>
      </section>

      {/* Why Amrita Sparsha Section */}
      <section className="py-16 bg-cream">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-dark-text mb-12" data-testid="why-section-heading">
            Why Amrita Sparsha
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center" data-testid="feature-doctor-guided">
              <div className="bg-golden w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-dark-text">Doctor-Guided Philosophy</h3>
              <p className="text-gray-700">Expert guidance from experienced homeopathic physicians</p>
            </div>
            
            <div className="text-center" data-testid="feature-clinical-care">
              <div className="bg-golden w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-dark-text">Clinical Care Aligned</h3>
              <p className="text-gray-700">Products aligned with professional clinical care</p>
            </div>
            
            <div className="text-center" data-testid="feature-root-cause">
              <div className="bg-golden w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-dark-text">Root-Cause Support</h3>
              <p className="text-gray-700">Focus on addressing root causes, not just symptoms</p>
            </div>
            
            <div className="text-center" data-testid="feature-wellness">
              <div className="bg-golden w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-dark-text">Long-Term Wellness</h3>
              <p className="text-gray-700">Safe, gentle approach for sustainable health</p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Pathways Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-dark-text mb-12" data-testid="pathways-heading">
            Two Clear Pathways
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Consult Pathway */}
            <div className="bg-cream p-8 rounded-lg shadow-lg" data-testid="pathway-consult">
              <h3 className="text-2xl font-bold text-golden mb-4">Consult a Doctor</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                For personalized homeopathic treatment and chronic concerns. Get individualized care that addresses your unique health needs.
              </p>
              <Link 
                to="/contact" 
                className="btn-primary px-6 py-3 rounded-lg font-semibold inline-block"
                data-testid="pathway-consult-button"
              >
                Book Consultation
              </Link>
            </div>

            {/* Products Pathway */}
            <div className="bg-cream p-8 rounded-lg shadow-lg" data-testid="pathway-products">
              <h3 className="text-2xl font-bold text-golden mb-4">Explore Wellness Products</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                For daily skin, hair, and self-care support. Discover products formulated to complement your wellness journey.
              </p>
              <Link 
                to="/products" 
                className="btn-primary px-6 py-3 rounded-lg font-semibold inline-block"
                data-testid="pathway-products-button"
              >
                Browse Products
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 golden-gradient">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-testid="cta-heading">
            Begin Your Wellness Journey Today
          </h2>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
            Experience gentle, effective healing through doctor-guided homeopathy and supportive wellness products.
          </p>
          <Link 
            to="/about" 
            className="bg-white text-golden px-8 py-4 rounded-lg font-semibold text-lg hover:bg-cream transition-colors inline-block"
            data-testid="cta-learn-more-button"
          >
            Learn About Our Approach
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
